import { Link } from "react-router-dom";
import { Bluetooth, Smartphone, BookOpen, Volume2, Lightbulb, Clock, BatteryCharging, ArrowRight } from "lucide-react";
import { Reveal } from "../components/motion";
import PhoneMockup from "../components/PhoneMockup";
import { HOW_IT_WORKS } from "../lib/content";

const TECH = [
  { icon: Bluetooth, title: "Bluetooth connection", desc: "Your Buddy pairs with the parent app in seconds. Once set, it remembers its bedtime settings all on its own." },
  { icon: Smartphone, title: "The parent app", desc: "One simple, bedtime-themed app controls everything — so the magic stays with your child and the controls stay with you." },
  { icon: BookOpen, title: "Story playback", desc: "Choose from bedtime adventures, fairytales, calming stories and character adventures, played through the Buddy's built-in speaker." },
  { icon: Volume2, title: "Sound controls", desc: "Music, nature sounds and white noise with fully adjustable volume — from a whisper to storytime loud." },
  { icon: Lightbulb, title: "Light controls", desc: "Pick from six soft glow colors and adjust brightness from candle-soft to reading-bright." },
  { icon: Clock, title: "Sleep timers", desc: "Set a timer and the lights and audio gently wind down with the night — no tip-toeing back in." },
  { icon: BatteryCharging, title: "Charging", desc: "Charges with the included cable. A full charge covers many bedtimes; full battery details ship with your Buddy." },
];

const PASTELS = ["#FBE7EF", "#FFF3D1", "#DFF3FF", "#EEE8FF"];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page" className="bg-cream">
      <section className="px-6 py-20 text-center lg:py-28">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-pinkdeep">How it works</p>
          <h1 className="mx-auto mt-5 max-w-3xl font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">
            Hug. Glow. Listen. Drift.
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-navy/55">
            The magic feels effortless for kids. Here's how it works for parents.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {HOW_IT_WORKS.map((c, i) => (
            <Reveal key={c.step} delay={i * 0.08}>
              <div className="h-full rounded-[2rem] p-8" style={{ background: PASTELS[i] }} data-testid={`hiw-step-${c.step.toLowerCase()}`}>
                <span className="font-display text-4xl font-medium text-navy/30">{c.num}</span>
                <h2 className="mt-3 font-display text-2xl font-semibold tracking-wide text-navy">{c.step}</h2>
                <p className="mt-3 text-sm leading-relaxed text-navy/60">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-lavender py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 lg:grid-cols-2">
          <PhoneMockup />
          <div>
            <Reveal>
              <h2 className="font-display text-3xl font-medium text-navy sm:text-4xl">The friendly tech underneath.</h2>
              <p className="mt-4 max-w-md text-base text-navy/60">No manuals needed. Everything a parent asks us, answered plainly:</p>
            </Reveal>
            <div className="mt-8 space-y-3">
              {TECH.map((t, i) => (
                <Reveal key={t.title} delay={i * 0.04}>
                  <div className="flex gap-4 rounded-2xl bg-white p-5 shadow-[0_6px_24px_rgba(16,42,86,0.05)]" data-testid={`tech-${t.title.toLowerCase().replace(/ /g, "-")}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blush text-pinkdeep"><t.icon size={19} /></span>
                    <div>
                      <h3 className="font-display font-semibold text-navy">{t.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-navy/55">{t.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 text-center">
        <Reveal>
          <h2 className="font-display text-3xl font-medium text-navy">Ready to meet the crew?</h2>
          <Link to="/buddy/luna-the-unicorn" data-testid="hiw-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-pink px-9 py-4 font-display font-semibold tracking-wide text-navy transition hover:scale-[1.04] hover:bg-moon">
            Find Their Buddy <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
